import React, { useState, useEffect, useCallback, FormEvent, ReactNode } from 'react';
import { Play, Pause, RotateCcw, Settings, Coffee, BookOpen } from 'lucide-react';

type Mode = 'focus' | 'shortBreak' | 'longBreak';

interface ModeButtonProps {
  targetMode: Mode;
  icon: ReactNode;
  children: ReactNode;
}

interface PomodoroTimerProps {
  onModeChange?: (mode: string, linkWithWhiteNoise?: boolean) => void;
}

const PomodoroTimer: React.FC<PomodoroTimerProps> = ({ onModeChange }) => {
  const [focusDuration, setFocusDuration] = useState<number>(25);
  const [shortBreakDuration, setShortBreakDuration] = useState<number>(5);
  const [longBreakDuration, setLongBreakDuration] = useState<number>(15);
  const [sessionsBeforeLongBreak, setSessionsBeforeLongBreak] = useState<number>(4);

  const [mode, setMode] = useState<Mode>('focus');
  const [timeLeft, setTimeLeft] = useState<number>(focusDuration * 60);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [sessionCount, setSessionCount] = useState<number>(0);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [linkWithWhiteNoise, setLinkWithWhiteNoise] = useState<boolean>(true);

  const calculateTimeLeft = useCallback((): number => {
    if (mode === 'focus') return focusDuration * 60;
    if (mode === 'shortBreak') return shortBreakDuration * 60;
    if (mode === 'longBreak') return longBreakDuration * 60;
    return focusDuration * 60;
  }, [focusDuration, shortBreakDuration, longBreakDuration, mode]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
  }, [focusDuration, shortBreakDuration, longBreakDuration, mode, calculateTimeLeft]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (isActive && timeLeft === 0) {
      if (interval) clearInterval(interval);
      setIsActive(false);
      
      // 播放提示音
      const notification = new Audio('/audio/notification.mp3');
      notification.play().catch(error => console.error("無法播放提示音:", error));

      if (mode === 'focus') {
        setSessionCount(prevCount => prevCount + 1);
        if ((sessionCount + 1) % sessionsBeforeLongBreak === 0) {
          setMode('longBreak');
        } else {
          setMode('shortBreak');
        }
      } else {
        setMode('focus');
        // 通知父組件模式變化為專注模式
        if (onModeChange) {
          onModeChange('focus', linkWithWhiteNoise);
        }
      }
      
      // 通知父組件模式變化
      if (onModeChange) {
        if (mode === 'focus') {
          if ((sessionCount + 1) % sessionsBeforeLongBreak === 0) {
            onModeChange('longBreak', linkWithWhiteNoise);
          } else {
            onModeChange('shortBreak', linkWithWhiteNoise);
          }
        }
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft, mode, sessionCount, sessionsBeforeLongBreak, calculateTimeLeft]);

  const toggleTimer = () => {
    const newActiveState = !isActive;
    setIsActive(newActiveState);
    
    // 如果是開始計時且處於專注模式，通知父組件
    if (newActiveState && mode === 'focus' && onModeChange) {
      onModeChange('focus', linkWithWhiteNoise);
    }
  };

  const resetTimer = useCallback(() => {
    setIsActive(false);
    setTimeLeft(calculateTimeLeft());
  }, [calculateTimeLeft]);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    setIsActive(false);
    
    // 通知父組件模式變化，並傳遞是否連動白噪音
    if (onModeChange) {
      onModeChange(newMode, linkWithWhiteNoise);
    }
  };
  
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSettingsSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isActive) {
        setTimeLeft(calculateTimeLeft());
    }
    setShowSettings(false);
  };

  const getModeDisplayName = (): string => {
    if (mode === 'focus') return '專注';
    if (mode === 'shortBreak') return '短歇';
    if (mode === 'longBreak') return '長休';
    return '';
  };

  const ModeButton: React.FC<ModeButtonProps> = ({ targetMode, icon, children }) => (
    <button
      onClick={() => handleModeChange(targetMode)}
      className={`px-4 py-2 rounded-md font-semibold transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-opacity-75 
        ${mode === targetMode 
          ? 'bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-lg focus:ring-teal-400' 
          : 'bg-gray-700 hover:bg-gray-600 text-gray-300 focus:ring-gray-500'}`}
      aria-label={`切換至${children}模式`}
    >
      {icon} {children}
    </button>
  );

  return (
    <div className="pomodoro-timer-container p-6 bg-gray-800 text-white rounded-xl shadow-2xl max-w-md mx-auto my-10 border border-gray-700">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">番茄時鐘</h2>
        <button 
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 rounded-full hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
          aria-label="開啟設定"
        >
          <Settings size={28} className="text-teal-400" />
        </button>
      </div>

      {showSettings && (
        <form onSubmit={handleSettingsSubmit} className="settings-panel mb-6 p-4 bg-gray-700 rounded-lg space-y-4 border border-gray-600">
          <h3 className="text-xl font-semibold text-teal-300 mb-3">時鐘設定</h3>
          {[ 
            {label: '專注 (分鐘)', value: focusDuration, setter: setFocusDuration, min:1, max:120},
            {label: '短歇 (分鐘)', value: shortBreakDuration, setter: setShortBreakDuration, min:1, max:30},
            {label: '長休 (分鐘)', value: longBreakDuration, setter: setLongBreakDuration, min:1, max:60},
            {label: '長休頻率 (次專注後)', value: sessionsBeforeLongBreak, setter: setSessionsBeforeLongBreak, min:1, max:10}
          ].map(setting => (
            <div key={setting.label} className="flex items-center justify-between">
              <label htmlFor={setting.label.replace(/\s/g, '')} className="text-gray-300">{setting.label}:</label>
              <input 
                type="number" 
                id={setting.label.replace(/\s/g, '')} 
                value={setting.value} 
                min={setting.min} 
                max={setting.max} 
                onChange={(e) => setting.setter(parseInt(e.target.value, 10) || setting.min)} 
                className="w-20 p-2 bg-gray-600 text-white rounded-md border border-gray-500 focus:ring-teal-400 focus:border-teal-400"
              />
            </div>
          ))}
          
          {/* 白噪音連動設定 */}
          <div className="flex items-center justify-between">
            <label className="text-gray-300">連動白噪音:</label>
            <div className="flex items-center">
              <label className="inline-flex items-center mr-4">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-teal-500"
                  checked={linkWithWhiteNoise}
                  onChange={() => setLinkWithWhiteNoise(true)}
                />
                <span className="ml-2 text-gray-300">開啟</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-teal-500"
                  checked={!linkWithWhiteNoise}
                  onChange={() => setLinkWithWhiteNoise(false)}
                />
                <span className="ml-2 text-gray-300">關閉</span>
              </label>
            </div>
          </div>
          
          <button type="submit" className="w-full mt-3 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-300">儲存設定</button>
        </form>
      )}

      <div className="mode-selector flex justify-around mb-8">
        <ModeButton targetMode="focus" icon={<BookOpen size={18} className="mr-1"/>}>專注</ModeButton>
        <ModeButton targetMode="shortBreak" icon={<Coffee size={18} className="mr-1"/>}>短歇</ModeButton>
        <ModeButton targetMode="longBreak" icon={<Coffee size={18} className="mr-1"/>}>長休</ModeButton>
      </div>

      <div className="timer-display text-8xl font-mono font-bold text-center my-8 text-teal-300 tracking-wider">
        {formatTime(timeLeft)}
      </div>
      <div className="text-center text-xl text-gray-400 mb-8">
        目前模式: <span className="font-semibold text-teal-400">{getModeDisplayName()}</span> | 第 {sessionCount + 1} 次專注
      </div>

      <div className="flex flex-col space-y-4">
        <div className="controls flex justify-center space-x-6">
          <button 
            onClick={toggleTimer} 
            className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-opacity-75 flex items-center
              ${isActive 
                ? 'bg-gradient-to-br from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white focus:ring-red-400' 
                : 'bg-gradient-to-br from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white focus:ring-green-400'}`}
            aria-label={isActive ? '暫停計時' : '開始計時'}
          >
            {isActive ? <Pause size={24} className="mr-2"/> : <Play size={24} className="mr-2"/>}
            {isActive ? '暫 停' : '開 始'}
          </button>
          <button 
            onClick={resetTimer} 
            className="px-8 py-3 rounded-lg font-bold text-lg bg-gray-600 hover:bg-gray-500 text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75 flex items-center"
            aria-label="重設計時器"
          >
            <RotateCcw size={24} className="mr-2"/>
            重 置
          </button>
        </div>
        
        {/* 白噪音連動狀態指示 */}
        <div className="flex justify-center">
          <div className={`text-sm px-3 py-1 rounded-full ${linkWithWhiteNoise ? 'bg-teal-800 text-teal-200' : 'bg-gray-700 text-gray-400'}`}>
            {linkWithWhiteNoise ? '專注時自動播放白噪音' : '不連動白噪音'}
            <button 
              onClick={() => setLinkWithWhiteNoise(!linkWithWhiteNoise)} 
              className="ml-2 text-xs underline hover:text-white"
              title={linkWithWhiteNoise ? '點擊關閉連動' : '點擊開啟連動'}
            >
              切換
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PomodoroTimer;